import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarClock, Loader2 } from 'lucide-react';
import { interviewsAPI } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { FORMATOS, ACTIVE_STATUS, toLocalInput } from './interviewUtils';

export const ScheduleInterviewDialog = ({ open, onOpenChange, jobId, candidateId, candidateName, interview, onSaved }) => {
  const [form, setForm] = useState({ data_hora: '', formato: 'presencial', local: '', observacoes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(interview
      ? { data_hora: toLocalInput(interview.data_hora), formato: interview.formato, local: interview.local || '', observacoes: interview.observacoes || '' }
      : { data_hora: '', formato: 'presencial', local: '', observacoes: '' });
  }, [open, interview]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.data_hora) { toast.error('Escolha a data e a hora'); return; }
    if (!form.local.trim()) { toast.error(`Informe o ${FORMATOS[form.formato].field.toLowerCase()}`); return; }
    setSaving(true);
    const payload = { ...form, data_hora: new Date(form.data_hora).toISOString() };
    try {
      const r = interview
        ? await interviewsAPI.reschedule(interview.id, payload)
        : await interviewsAPI.schedule({ ...payload, job_id: jobId, candidate_id: candidateId });
      toast.success(interview ? 'Entrevista remarcada. O candidato foi avisado.' : 'Entrevista agendada. O candidato foi avisado no app e por e-mail.');
      onOpenChange(false);
      onSaved && onSaved(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar entrevista');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="schedule-interview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarClock className="w-5 h-5 text-cyan-600" />{interview ? 'Remarcar entrevista' : 'Agendar entrevista'}</DialogTitle>
          {candidateName && <DialogDescription>Com {candidateName}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Data e hora</Label>
            <Input type="datetime-local" value={form.data_hora} onChange={(e) => set('data_hora', e.target.value)} data-testid="interview-datetime-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Formato</Label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(FORMATOS).map(([k, f]) => (
                <button key={k} type="button" onClick={() => set('formato', k)} data-testid={`interview-format-${k}`}
                  className={`rounded-lg border px-2 py-2 text-sm transition-colors ${form.formato === k ? 'border-cyan-600 bg-cyan-50 text-cyan-800 font-medium' : 'border-slate-200 hover:bg-slate-50'}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{FORMATOS[form.formato].field}</Label>
            <Input value={form.local} onChange={(e) => set('local', e.target.value)} placeholder={FORMATOS[form.formato].placeholder} data-testid="interview-local-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Observações para o candidato (opcional)</Label>
            <Textarea rows={2} value={form.observacoes} onChange={(e) => set('observacoes', e.target.value)} placeholder="Ex.: trazer documento com foto" data-testid="interview-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Voltar</Button>
          <Button onClick={save} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700" data-testid="interview-save-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{interview ? 'Remarcar' : 'Agendar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ScheduleInterviewButton = ({ jobId, candidateId, candidateName, onSaved, size = 'sm', refreshKey }) => {
  const [open, setOpen] = useState(false);
  const [hasOpen, setHasOpen] = useState(false);
  useEffect(() => {
    interviewsAPI.list({ job_id: jobId, candidate_id: candidateId })
      .then((r) => setHasOpen(r.data.some((i) => ACTIVE_STATUS.includes(i.status)))).catch(() => {});
  }, [jobId, candidateId, refreshKey]);
  if (hasOpen) return <span className="text-xs text-cyan-700 flex items-center gap-1" data-testid={`interview-scheduled-${jobId}-${candidateId}`}><CalendarClock className="w-3 h-3" />Entrevista agendada</span>;
  return (
    <>
      <Button size={size} variant="outline" onClick={() => setOpen(true)} className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
        data-testid={`schedule-interview-${jobId}-${candidateId}`}>
        <CalendarClock className="w-4 h-4 mr-1" />Agendar entrevista
      </Button>
      <ScheduleInterviewDialog open={open} onOpenChange={setOpen} jobId={jobId} candidateId={candidateId} candidateName={candidateName}
        onSaved={(d) => { setHasOpen(true); onSaved && onSaved(d); }} />
    </>
  );
};
